<script setup>
import { useUserStore } from '@/stores/user';
import { useCompanyStore } from '@/stores/company';
import { ref, computed } from 'vue';
import AppContainer from '@/components/AppContainer.vue';
import { topMenu } from '@/config/topMenu';
import { useDisplay } from 'vuetify';

const user = useUserStore();
const companyStore = useCompanyStore();
const activeMenuItem = ref('');
let showMobileMenu = ref(false);
const { smAndDown } = useDisplay();
const ITEMS_PER_COLUMN = 5;

// Check if an item should be hidden in production
function isHiddenInProduction(item) {
    return item.hideInProduction && import.meta.env.VITE_ENV === 'prod';
}

// Check if an integration is enabled for the company
function isIntegrationEnabled(integrationKey) {
    if (!integrationKey) return true; // Si no tiene integrationKey, siempre mostrar

    // // MOCK: Cambiar este valor para probar (true = mostrar, false = ocultar)
    // const MOCK_ZOOM_CARRIER = false; // Cambiar a false para ocultar el menú

    // if (integrationKey === 'ZOOM_CARRIER') {
    //     return MOCK_ZOOM_CARRIER;
    // }

    const externalIntegration = companyStore.data?.externalIntegration;
    return externalIntegration?.[integrationKey] === true;
}

// Filter group items by user roles, production, and integrations
function filterGroupItems(groupItems, userRoles) {
    return groupItems
        .filter(
            (item) =>
                !isHiddenInProduction(item) &&
                (!item.role ||
                    item.role.some((role) => userRoles.includes(role))) &&
                isIntegrationEnabled(item.integrationKey)
        )
        .sort((a, b) => a.label.localeCompare(b.label));
}

// Filter submenus (groups and direct links)
function filterSubMenu(subMenu, userRoles) {
    return (
        subMenu
            .filter((subItem) => !isHiddenInProduction(subItem))
            .map((subItem) => {
                if (subItem.isGroup && subItem.groupItems) {
                    const filteredGroupItems = filterGroupItems(
                        subItem.groupItems,
                        userRoles
                    );
                    return { ...subItem, groupItems: filteredGroupItems };
                }
                return subItem;
            })
            // Remove empty groups
            .filter(
                (subItem) =>
                    !subItem.isGroup ||
                    (subItem.groupItems && subItem.groupItems.length > 0)
            )
            .sort((a, b) => {
                if (!a.label) return 1;
                if (!b.label) return -1;
                return a.label.localeCompare(b.label);
            })
    );
}

// Filter the top menu by user roles and production
function getFilteredTopMenu(topMenu, userRoles) {
    return (
        topMenu.system
            .filter((item) => !isHiddenInProduction(item))
            .map((item) => {
                if (!item.subMenu) return item;
                const filteredSubMenu = filterSubMenu(item.subMenu, userRoles);
                return { ...item, subMenu: filteredSubMenu };
            })
            // Remove main menu items with empty submenus
            .filter((item) => !item.subMenu || item.subMenu.length > 0)
    );
}

const userRoles = user.data?.roles || [];
const filteredTopMenu = computed(() => getFilteredTopMenu(topMenu, userRoles));

// Organize group items into columns for display
function getGroupItemColumns(groupItems) {
    if (!groupItems || !groupItems.length) return [];
    const columns = [];
    for (let i = 0; i < groupItems.length; i += ITEMS_PER_COLUMN) {
        columns.push(groupItems.slice(i, i + ITEMS_PER_COLUMN));
    }
    return columns;
}

function toggleMenuItem(menuItem) {
    activeMenuItem.value = menuItem === activeMenuItem.value ? '' : menuItem;
}

function getMenuItemClasses(menuItem) {
    if (!menuItem.subMenu) {
        return ['direct-link'];
    }
    let classes = [`${menuItem.key}-menu-item`];
    if (menuItem.key === activeMenuItem.value) {
        classes.push('active');
    }
    return classes;
}

function closeSubMenu(menuItem) {
    if (smAndDown.value) return;
    if (menuItem.key !== activeMenuItem.value) return;
    activeMenuItem.value = '';
}
</script>

<template>
    <AppContainer class="d-flex align-center">
        <p class="text-h6 company-title">
            {{ user.data?.company?.name ?? '' }}
        </p>

        <v-divider vertical inset class="mx-5"></v-divider>

        <v-spacer v-if="smAndDown"></v-spacer>
        <v-btn
            v-if="smAndDown"
            icon="mdi-menu"
            variant="plain"
            @click="showMobileMenu = !showMobileMenu"
        >
        </v-btn>

        <ul class="top-menu-bar" :class="{ active: showMobileMenu }">
            <li
                v-for="menuItem of filteredTopMenu"
                :key="menuItem.label"
                :class="getMenuItemClasses(menuItem)"
                @click="menuItem.subMenu ? toggleMenuItem(menuItem.key) : ''"
            >
                <router-link v-if="!menuItem.subMenu" :to="menuItem.to">{{
                    $t(menuItem.label)
                }}</router-link>

                <template v-else>
                    <span class="menu-item-label">{{
                        $t(menuItem.label)
                    }}</span>
                    <v-icon
                        :icon="
                            activeMenuItem === menuItem.key
                                ? 'mdi-chevron-up'
                                : 'mdi-chevron-down'
                        "
                    ></v-icon>
                    <ul
                        v-if="activeMenuItem === menuItem.key || smAndDown"
                        class="submenu"
                        v-click-outside="
                            () => (menuItem ? closeSubMenu(menuItem) : '')
                        "
                    >
                        <AppContainer class="d-flex">
                            <li
                                v-for="subMenuItem of menuItem.subMenu"
                                :key="subMenuItem.label"
                                :class="{ group: subMenuItem.isGroup }"
                            >
                                <template v-if="subMenuItem.isGroup">
                                    <label class="group-label">{{
                                        $t(subMenuItem.label)
                                    }}</label>
                                    <div class="group-columns-container">
                                        <div
                                            v-for="(
                                                column, colIndex
                                            ) in getGroupItemColumns(
                                                subMenuItem.groupItems
                                            )"
                                            :key="colIndex"
                                            class="group-column"
                                        >
                                            <ul>
                                                <li
                                                    v-for="groupItem of column"
                                                    :key="groupItem.label"
                                                >
                                                    <router-link
                                                        :to="groupItem.to"
                                                        >{{
                                                            $t(groupItem.label)
                                                        }}</router-link
                                                    >
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </template>
                                <router-link v-else :to="subMenuItem.to">{{
                                    $t(subMenuItem.label)
                                }}</router-link>
                            </li>
                        </AppContainer>
                    </ul>
                </template>
            </li>
        </ul>
    </AppContainer>
</template>

<style lang="scss">
.company-title {
    font-weight: 700;
    color: rgb(var(--v-theme-primary));
}

ul {
    font-size: 14px !important;
    display: flex;
    list-style: none;

    li {
        margin: 0 1.2rem;
        color: #505c6d;

        &:hover {
            cursor: pointer;

            .menu-item-label {
                text-decoration: underline;
                color: rgb(var(--v-theme-primary));
            }
        }

        .menu-item-label {
            color: #505c6d;
            transition: color 0.2s ease;
        }

        ul {
            display: none;
            position: absolute;
            background-color: #f6f7f8;
            top: 62px;
            width: 100%;
            left: 0px;
            box-shadow: 0 5px 5px 0px #d8d8d8;
            padding: 10px 0px;

            li {
                margin: 0px;
            }
        }
    }

    li.active {
        .menu-item-label {
            color: rgb(var(--v-theme-primary));
            text-decoration: underline;
        }

        color: #212121;

        ul {
            display: flex;
            flex-wrap: wrap;
            font-weight: normal;

            li {
                text-decoration: none;
                margin-right: 1.5rem;
                margin-bottom: 0.75rem;

                a {
                    text-decoration: none;
                    color: rgb(var(--v-theme-primary));
                    white-space: nowrap;
                    font-size: 14px !important;

                    &:hover {
                        text-decoration: underline;
                    }
                }
            }

            li.group {
                .group-label {
                    font-size: 0.8rem;
                    color: #505050;
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .group-columns-container {
                    display: flex;
                    flex-wrap: wrap;
                }

                .group-column {
                    margin-right: 6px;
                }

                ul {
                    min-width: 100px;
                    padding: 2px 0px 0px 0px;
                    position: initial;
                    display: block;
                    box-shadow: none;
                }

                ul li {
                    margin-bottom: 4px;
                }

                ul li:last-child {
                    margin-bottom: 0;
                }
            }
        }
    }

    li.direct-link {
        a {
            text-decoration: none;
            color: #505c6d;
            font-size: 14px !important;
            transition: color 0.2s ease;

            &:hover {
                text-decoration: underline;
                color: rgb(var(--v-theme-primary));
            }
        }
    }
}

@media (max-width: 960px) {
    .top-menu-bar {
        position: absolute;
        top: 62px;
        width: 50%;
        height: calc(100vh - 62px);
        right: -50%;
        background-color: white;
        flex-direction: column;
        transition: right 0.2s ease-out;
        overflow: scroll;

        li {
            color: rgba(0, 0, 0, 0.87) !important;
            position: relative;
            padding: 15px 0px;

            &.active {
                text-decoration: none;

                ul {
                    top: 20px;

                    li {
                        a {
                            padding: 0px;
                        }
                    }
                }
            }

            &.direct-link {
                a {
                    padding: 0px;
                }
            }

            a {
                display: block;
                padding: 23px 0px;
                color: rgba(0, 0, 0, 0.87) !important;
            }

            ul {
                display: none;
                position: initial;
                background-color: #fff;
                width: 100%;
                left: 0px;
                box-shadow: none;
                padding: 10px 0px;

                li {
                    margin: 0px;
                }
            }
        }

        .submenu > .app-container {
            flex-direction: column;
        }

        .submenu {
            padding: 10px 0px 0px 0px;
        }

        .submenu .group {
            padding: 0px;
            margin-bottom: 20px;
        }

        .group-columns-container {
            flex-direction: column;
        }

        .group-column {
            margin-bottom: 10px;
        }
    }

    .top-menu-bar.active {
        right: 0;
        box-shadow: -1px 22px 22px rgba(0, 0, 0, 0.5);
    }
}
</style>
